import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { getDiffDays } from "@/utils/time";
import useRequest from "@/utils/useReq";
import { Skeleton } from "antd";
import classNames from "classnames";
import { round } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  cancelUnPayOrder,
  getGrocery,
  getVipGoods,
  orderCalculate,
} from "../../../services";
import PayBottom from "../pay/payBottom";
import { VipDialogContext } from "../shared/configContext";
import {
  getLastExpireTime,
  getMaxBuyNumber,
  getPayDetailByGood,
} from "../shared/utils";
import styles from "./index.module.less";

export default function PkgDetail({
  userRight,
  defaultSelectedPkg,
  onOrderDone,
  onTransfer,
  ...rest
} = {}) {
  const vipGoods = useRequest(getVipGoods);

  const { data: dialogConf } = useRequest(async () =>
    getGrocery("auth-extend-dialog")
  );

  useEffect(() => {
    cancelUnPayOrder();
  }, []);

  if (!userRight || vipGoods.loading) {
    // 占位元素
    return <div className={styles.detailsBlock}></div>;
  }

  return (
    <div {...rest} className={styles.details}>
      <div className={styles.container}>
        <AuthExtendTab
          defaultSelectedPkg={defaultSelectedPkg}
          goods={vipGoods.data.list}
          vipType="company"
          conf={dialogConf}
          onOrderDone={onOrderDone}
          onTransfer={onTransfer}
        />
      </div>
    </div>
  );
}

function AuthExtendTab({
  goods,
  defaultSelectedPkg,
  vipType,
  conf,
  onUpdate,
  onOrderDone,
  onTransfer,
}) {
  const { rightData } = useContext(VipDialogContext);
  const [selectedPkg, setSelectedPkg] = useState();
  // 处理显示的卡片数据，部分配置，部分需要计算
  const { data: cardList } = useRequest(
    async () => {
      if (conf && rightData) {
        const result = [];

        for (let i = 0; i < conf[vipType].length; i++) {
          const item = conf[vipType][i];
          const good = goods.find((g) => g.code === item.goodCode);
          const sku = good.skus[0];
          const right_package_code = sku.right_packages[0].identification;
          const matchRights = rightData.effect_rights.filter(
            (right) => right_package_code === right.right_package_code
          );

          const obj = {
            ...item,
            sku,
          };

          // 购买限制，不超过会员周期
          obj.max =
            getMaxBuyNumber(matchRights, rightData.vips[0], sku) > 0 ? 1 : 0;

          // 当剩余会员时长不足一个商品时间周期时
          // todo：剩余时长不同，还得用不同的商品进行折扣计算
          if (obj.max < 1) {
            const calulateInfo = await orderCalculate({
              scene: "addition",
              skus: [
                {
                  sku_id: sku.id,
                  quantity: 1,
                },
              ],
              promotion: {
                activities: [],
                user_coupon_ids: [],
              },
            });

            if (sku.price == calulateInfo.actual_total_price) {
              obj.max = 1;
            }

            if (
              !calulateInfo.err &&
              sku.price != calulateInfo.actual_total_price
            ) {
              // 计算折算的天数
              calulateInfo.days = round(
                getDiffDays(
                  rightData.vip_expire,
                  getLastExpireTime(matchRights)
                ),
                2
              );

              obj.max = 1;
              obj.calulate = calulateInfo;
            }
          }

          // 预先计算好支付信息
          obj.detail = getPayDetailByGood(good, 1, {
            start: getLastExpireTime(matchRights),
            ...(obj.calulate
              ? {
                  end: rightData.vip_expire,
                  extend: { price: obj.calulate.actual_total_price },
                }
              : {}),
          });

          if (obj.max) result.push(obj);
        }

        if (!selectedPkg && defaultSelectedPkg) {
          const selectCard = result.find((c) =>
            defaultSelectedPkg.includes(c.goodCode)
          );
          setSelectedPkg(selectCard);
        }

        return result;
      }
    },
    { refreshDeps: [rightData, conf, goods] }
  );

  const payDetails = useMemo(() => {
    if (cardList && cardList.length) {
      return [selectedPkg ? selectedPkg.detail : cardList[0].detail];
    }
    return null;
  }, [cardList, selectedPkg]);

  return (
    <>
      <div className={styles.left}>
        <div className={styles.title}>升级授权范围</div>
        <div className={styles.subTitle}>当前授权范围</div>
        <div className={styles.content}>
          {rightData.auth_rights?.length ? "企业高级授权" : "企业标准授权"}
        </div>
        <div className={classNames(styles.subTitle, styles.primaryText)}>
          购买后授权范围
        </div>
        <div className={styles.content}>企业高级授权</div>
        <a
          href="/vip/info?anchor=discoveryRange"
          className={styles.btn}
          target="_blank"
        >
          对比授权范围
        </a>
      </div>
      <div className={styles.right} data-order-type="升级授权">
        <div className={styles.pkgs}>
          {cardList ? (
            <>
              {cardList.map((item, index) => (
                <Card
                  active={item === selectedPkg || (!selectedPkg && index === 0)}
                  conf={item}
                  key={item.goodCode}
                  calc={item.calulate}
                  price={
                    item.calulate
                      ? item.calulate.actual_total_price
                      : item.sku.price
                  }
                  onClick={() => setSelectedPkg(item)}
                  expireTime={item.detail.expireTime}
                />
              ))}
              {vipType === "personal" && (
                <UpgradeCard conf={conf.upgrade} onUpdate={onUpdate} />
              )}
            </>
          ) : (
            <div className={styles.pkgCard}>
              <Skeleton.Avatar shape="circle" size={"large"} />
              <Skeleton active paragraph={4} />
            </div>
          )}
        </div>

        <PayBottom
          details={payDetails}
          scene="addition"
          onOrderDone={onOrderDone}
          onTransfer={onTransfer}
        />
      </div>
    </>
  );
}

function Card({ price, conf, active, expireTime, calc, onClick }) {
  return (
    <div
      className={classNames(styles.pkgCard, { [styles.active]: active })}
      onClick={onClick}
    >
      <Image
        width={48}
        {...gdImgSrcPolicy(conf.img[0].url, {
          displayWidth: 48,
          sharpen: 100,
          type: "png",
        })}
        alt="商品图片"
      />

      <div
        className={styles.title}
        dangerouslySetInnerHTML={{ __html: conf.name }}
      ></div>
      <div className={styles.desc}>
        {conf.desc}
        {!conf.isTemPkg && `，${expireTime}到期`}
      </div>
      <div className={styles.price}>
        ¥<span>{price}</span>
        {conf.isTemPkg || (calc ? `(${calc.days}天)` : "/年")}
      </div>
    </div>
  );
}

function UpgradeCard({ conf, onUpdate }) {
  return (
    <div className={styles.upgradeCard}>
      <div
        className={styles.title}
        dangerouslySetInnerHTML={{ __html: conf.title }}
      ></div>
      <div className={styles.desc}>{conf.desc}</div>
      <a onClick={onUpdate}>
        {conf.btnTxt} <HBIcon type="ic_open" />
      </a>
    </div>
  );
}
